import type { CompanyImagesProps } from '@modules/professional/component/about-tab/company-images/CompanyImages';
import { useState } from 'react';
import {
    Button,
    Grid,
    Image,
    pxArrayToRem,
    useStyles,
} from '@archipro-design/aria';
import PageSection from '@modules/professional/component/page-layout/PageSection';
import * as S from '@modules/professional/component/about-tab/company-images/CompanyImages.style';
import PhotoViewerModal from '@modules/professional/component/about-tab/company-images/CompanyPhotoViewer';
import { useLoaderData } from '@remix-run/react';
import type { InfoPageLoaderData } from '@modules/professional/api/info-page-loader.server';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { useTracker } from '@archipro-website/tracker';

export const CompanyImagesDesktop: React.FC<CompanyImagesProps> = ({
    imagePlacement,
    countRemaining,
    isLastImageLandscape,
    companyImages,
    isPortraitOnly,
    disablePhotoViewer,
}) => {
    const [activeId, setActiveId] = useState<number | null>(null);

    const { css } = useStyles({
        isPortraitOnly,
        placementCount: imagePlacement.length,
    });

    const tracker = useTracker();

    const columns = isLastImageLandscape
        ? pxArrayToRem([497, 665, 328])
        : Math.min(imagePlacement.length, 3);

    const images = imagePlacement.filter(Boolean) ?? [];

    const { blockSummary } = useLoaderData<InfoPageLoaderData>();
    const {
        Title,
        CompanyLogo,
        LogoBackgroundColor,
        ID: professionalID,
    } = useProfessionalInitialRouteData();

    return (
        <PageSection style={{ gridGap: 0 }}>
            <div className={css(S.PlacementGridContainer)}>
                <Grid columns={columns} className={css(S.PlacementGrid)}>
                    {images.map((img, index) => {
                        const { height, width, src, ID } = img;
                        const isLastImage = index === imagePlacement.length - 1;
                        const canViewMore = countRemaining > 0 && isLastImage;

                        return (
                            <div key={index} className={css(S.ImageWrap)}>
                                <Image
                                    src={src}
                                    width={width}
                                    height={height}
                                    overlay={
                                        disablePhotoViewer
                                            ? undefined
                                            : {
                                                  onClick: () => {
                                                      tracker.log(
                                                          'CompanyImageViewer',
                                                          {
                                                              url: new URL(
                                                                  window.location.href
                                                              ),
                                                              targetTracker:
                                                                  'archiproTracker',
                                                              data: {
                                                                  ExtraData:
                                                                      img.ID,
                                                              },
                                                          }
                                                      );
                                                      tracker.log(
                                                          'PhotoViewerTrigger',
                                                          {
                                                              url: new URL(
                                                                  window.location.href
                                                              ),
                                                              data: {
                                                                  LibraryItemID:
                                                                      professionalID ||
                                                                      0,
                                                              },
                                                          }
                                                      );
                                                      setActiveId(ID);
                                                  },
                                              }
                                    }
                                    alt={Title}
                                    layout="responsive"
                                    variables={{
                                        borderRadius: '0.625rem',
                                    }}
                                />
                                {canViewMore && (
                                    <Button
                                        className={css(S.ViewMore)}
                                        size={16}
                                        onClick={() => {
                                            tracker.log(
                                                'ProfessionalAllImageClick',
                                                {
                                                    url: new URL(
                                                        window.location.href
                                                    ),
                                                    targetTracker:
                                                        'archiproTracker',
                                                }
                                            );
                                            setActiveId(ID);
                                        }}
                                        variables={{
                                            buttonPadding: 0,
                                        }}
                                        transparent={true}
                                    >{`View ${countRemaining} more`}</Button>
                                )}
                            </div>
                        );
                    })}
                </Grid>
            </div>
            {activeId && (
                <PhotoViewerModal
                    professional={{
                        id: professionalID,
                        title: Title,
                        companyLogoSrc: CompanyLogo,
                        logoBackgroundColor: LogoBackgroundColor,
                        blockSummary,
                    }}
                    stack={companyImages}
                    imageId={activeId}
                    open={true}
                    toggle={() => setActiveId(null)}
                />
            )}
        </PageSection>
    );
};
